import {cities} from "domain/cities/cities";
import {Action, Seed} from "domain/aangifte";
import {array, either, map, option, record} from "fp-ts";
import {flow, identity, pipe} from "fp-ts/lib/function";
import {Value} from "domain/types";
import {runBurgerlijkeStaatRules} from "./rubrieken/burgerlijkeStaatRules";
import {rubriekenFp} from "domain/rubriek";
import {runGezinsLasteRules} from "./rubrieken/gezinsLasteRules";
import {runMutuallyExclusiveFieldRules} from "./rubrieken/mutuallyExclusiveFieldRules";

export interface UpdateRubriekValueAction {
  type: "UPDATE_RUBRIEK_VALUE";
  value?: Value;
  code: string;
}

export interface InvalidRubriekCode {
  type: "INVALID_RUBRIEK_CODE";
  code: string;
}

export interface InvalidValueType {
  type: "INVALID_VALUE_TYPE";
  expected: string;
  actual: string;
}

type RubriekError = InvalidValueType | InvalidRubriekCode;

export type Rubriek = {
  internalRubriek?: true;
  value?: Value;
  disabled?: boolean;
  name: {
    nl: string;
    fr: string;
  };
  default: string;
  type: "" | "bool" | "num" | "date" | "pct" | "buitenland";
  mar: "" | "1";
  mutExclWith: number[];
};

export type RubriekReducer = Map<string, Rubriek>;

export interface RubriekenSeed {
  [code: string]: Value;
}

const loadRubrieken = (postalcode: string) =>
  pipe(
    postalcode,
    cities.tryFind,
    option.map((city) =>
      pipe(
        require(`../taxCodes/taxCodes_${city.gewest}.json`),
        record.toArray,
        (codes) => new Map(codes) as RubriekReducer
      )
    )
  );

const defaultRubriekenSeed = {
  1002: true,
  3003: true,
};

const provisionRubrieken = (rubriekenSeed: RubriekenSeed = defaultRubriekenSeed) => (rubrieken: RubriekReducer) => {
  return pipe(
    Object.entries(rubriekenSeed),
    array.reduce(new Map(rubrieken), (acc, [code, value]) => {
      return pipe(
        acc,
        rubriekenFp.lookup(code),
        option.map((rubriek) => ({ ...rubriek, value })),
        option.map((newRubriek) => {
          acc.set(code, newRubriek);
          return acc;
        }),
        option.fold(() => acc, identity)
      );
    })
  );
};

export const init = (seed?: Seed): RubriekReducer => {
  return pipe(
    option.of(provisionRubrieken),
    option.ap(option.some(seed?.rubrieken)),
    option.ap(pipe(option.of(loadRubrieken), option.ap(option.fromNullable(seed?.postalcode)), option.flatten)),
    option.map(runBurgerlijkeStaatRules({ type: "INIT" })),
    option.getOrElse(() => new Map())
  );
};

const updateRubriekValue = (value?: Value) => (rubriek: Rubriek): either.Either<InvalidValueType, Rubriek> => {
  if (value === undefined) return either.right({ ...rubriek, value });
  switch (rubriek.type) {
    case "num":
      if (typeof value !== "number")
        return either.left({ type: "INVALID_VALUE_TYPE", actual: typeof value, expected: "number" });

      return either.right({ ...rubriek, value: Math.abs(Math.round(value)) });
    case "pct":
    case "":
      if (typeof value !== "number")
        return either.left({ type: "INVALID_VALUE_TYPE", actual: typeof value, expected: "number" });

      return either.right({ ...rubriek, value });

    case "bool":
      if (typeof value !== "boolean")
        return either.left({ type: "INVALID_VALUE_TYPE", actual: typeof value, expected: "boolean" });

      return either.right({ ...rubriek, value: value === true ? true : undefined });
    default:
      return either.right({ ...rubriek, value });
  }
};

const updateRubriek = <E>(code: string, f: (rubriek: Rubriek) => either.Either<E, Rubriek>) => (
  rubrieken: RubriekReducer
) => {
  return pipe(
    rubrieken,
    rubriekenFp.lookup(code),
    option.map(f),
    option.map(either.map((rubriek) => rubriekenFp.upsertAt(code, rubriek)(rubrieken)))
  );
};

const mergeRubrieken = (prevRubrieken: RubriekReducer) => (nextRubrieken: RubriekReducer) => {
  if (rubriekenFp.isEmpty(prevRubrieken)) {
    return pipe(nextRubrieken, provisionRubrieken(defaultRubriekenSeed));
  }
  return pipe(
    nextRubrieken,
    map.mapWithIndex((code, nextRubriek) => {
      return pipe(
          prevRubrieken,
          rubriekenFp.lookup(code),
          option.getOrElse(() => nextRubriek),
          r => ({...r, name: nextRubriek.name}) // always use new name
      );
    })
  );
};

export const update = (action: Action) => (rubrieken: RubriekReducer): either.Either<RubriekError, RubriekReducer> => {
  switch (action.type) {
    case "UPDATE_RUBRIEK_VALUE":
      return pipe(
        rubrieken,
        updateRubriek(action.code, updateRubriekValue(action.value)),
        either.fromOption<RubriekError>(() => ({ type: "INVALID_RUBRIEK_CODE", code: action.code })),
        either.flatten,
        either.map(
          flow(runMutuallyExclusiveFieldRules(action), runBurgerlijkeStaatRules(action), runGezinsLasteRules(action))
        )
      );
    case "UPDATE_POSTALCODE":
      return pipe(
        action.postalcode,
        loadRubrieken,
        option.map(mergeRubrieken(rubrieken)),
        option.map(runBurgerlijkeStaatRules(action)),
        option.getOrElse(() => rubrieken),
        either.right
      );
    case "RESET":
      return either.right(new Map());
    default:
      return either.right(rubrieken);
  }
};
